import React from "react";
import classNames from "classnames";
import { Entity } from "@common/types/entity";
import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { Button } from "@design-system/Button/Button";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { buildReportingConsolidationRoute } from "@components/reporting/reportingRoutes";
import { OwnershipCanvasZoomControls } from "./canvas/OwnershipCanvasZoomControls";
import { getWhollyOwnedBusinessSubsidiaryUuids } from "./ownershipGraph.helpers";
import { OwnershipSelection } from "./ownership.types";
import styles from "./OwnershipCommandBar.module.scss";

interface ConsolidateInput {
    parentEntityUuid: string;
    memberEntityUuids: string[];
}

interface OwnershipCommandBarProps {
    selection: OwnershipSelection;
    entitiesByUuid: Map<string, Entity>;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    isLayoutFrozen: boolean;
    onResetLayout: () => void;
    onAddRelationship: () => void;
    onConsolidate: (input: ConsolidateInput) => void;
    onUnconsolidate: (groupId: string) => void;
    onEditRelationship: (
        relationship: ConsolidationRelationshipResponse,
    ) => void;
    onDeleteRelationship: (relationshipId: string) => void;
    onNavigate: (url: string) => void;
}

interface CommandAction {
    key: string;
    label: string;
    onClick: () => void;
}

export const OwnershipCommandBar: React.FC<OwnershipCommandBarProps> = ({
    selection,
    entitiesByUuid,
    relationships,
    groups,
    isLayoutFrozen,
    onResetLayout,
    onAddRelationship,
    onConsolidate,
    onUnconsolidate,
    onEditRelationship,
    onDeleteRelationship,
    onNavigate,
}) => {
    const selectedEntity =
        selection?.kind === "entity"
            ? entitiesByUuid.get(selection.entityUuid)
            : undefined;
    const selectedRelationship =
        selection?.kind === "relationship"
            ? relationships.find(({ id }) => id === selection.relationshipId)
            : undefined;
    const selectedGroup =
        selection?.kind === "group"
            ? groups.find(({ id }) => id === selection.groupId)
            : undefined;
    const actions = resolveActions({
        selectedEntity,
        selectedRelationship,
        selectedGroup,
        entitiesByUuid,
        relationships,
        groups,
        onAddRelationship,
        onConsolidate,
        onUnconsolidate,
        onEditRelationship,
        onDeleteRelationship,
        onNavigate,
    });

    return (
        <div className={styles.footer}>
            <CommandIsland
                className={classNames(
                    commandIslandStyles.bulkActionsStickyFooter,
                    styles.commandIsland,
                )}
            >
                <div className={styles.bar}>
                    <div className={styles.referenceSection}>
                        <OwnershipCanvasZoomControls
                            isLayoutFrozen={isLayoutFrozen}
                            onResetLayout={onResetLayout}
                        />
                    </div>
                    {actions.length > 0 && (
                        <div className={styles.actionsSection}>
                            {actions.map((action) => (
                                <Button
                                    key={action.key}
                                    variant="primary"
                                    size="md"
                                    onClick={action.onClick}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            </CommandIsland>
        </div>
    );
};

interface ResolveActionsParams {
    selectedEntity: Entity | undefined;
    selectedRelationship: ConsolidationRelationshipResponse | undefined;
    selectedGroup: ConsolidationGroupResponse | undefined;
    entitiesByUuid: Map<string, Entity>;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    onAddRelationship: () => void;
    onConsolidate: (input: ConsolidateInput) => void;
    onUnconsolidate: (groupId: string) => void;
    onEditRelationship: (
        relationship: ConsolidationRelationshipResponse,
    ) => void;
    onDeleteRelationship: (relationshipId: string) => void;
    onNavigate: (url: string) => void;
}

function resolveActions({
    selectedEntity,
    selectedRelationship,
    selectedGroup,
    entitiesByUuid,
    relationships,
    groups,
    onAddRelationship,
    onConsolidate,
    onUnconsolidate,
    onEditRelationship,
    onDeleteRelationship,
    onNavigate,
}: ResolveActionsParams): CommandAction[] {
    if (selectedGroup) {
        return buildGroupActions({
            group: selectedGroup,
            onUnconsolidate,
            onNavigate,
        });
    }
    if (selectedEntity) {
        return buildEntityActions({
            entity: selectedEntity,
            entitiesByUuid,
            relationships,
            groups,
            onAddRelationship,
            onConsolidate,
            onUnconsolidate,
        });
    }
    if (selectedRelationship) {
        return buildRelationshipActions({
            relationship: selectedRelationship,
            onEditRelationship,
            onDeleteRelationship,
        });
    }
    return [];
}

interface BuildEntityActionsParams {
    entity: Entity;
    entitiesByUuid: Map<string, Entity>;
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
    onAddRelationship: () => void;
    onConsolidate: (input: ConsolidateInput) => void;
    onUnconsolidate: (groupId: string) => void;
}

function buildEntityActions({
    entity,
    entitiesByUuid,
    relationships,
    groups,
    onAddRelationship,
    onConsolidate,
    onUnconsolidate,
}: BuildEntityActionsParams): CommandAction[] {
    const parentGroup = entity.isBusiness
        ? groups.find(
              ({ parentEntityUuid }) => parentEntityUuid === entity.uuid,
          )
        : undefined;
    const actions: CommandAction[] = [
        {
            key: "add-relationship",
            label: "Add relationship",
            onClick: onAddRelationship,
        },
    ];

    if (parentGroup) {
        actions.push({
            key: "unconsolidate",
            label: "Unconsolidate",
            onClick: () => onUnconsolidate(parentGroup.id),
        });
        return actions;
    }
    if (!entity.isBusiness) {
        return actions;
    }

    const memberEntityUuids = getWhollyOwnedBusinessSubsidiaryUuids({
        parentEntityUuid: entity.uuid,
        relationships,
        entitiesByUuid,
    });
    if (memberEntityUuids.length > 0) {
        actions.push({
            key: "consolidate",
            label: "Consolidate",
            onClick: () =>
                onConsolidate({
                    parentEntityUuid: entity.uuid,
                    memberEntityUuids,
                }),
        });
    }
    return actions;
}

interface BuildGroupActionsParams {
    group: ConsolidationGroupResponse;
    onUnconsolidate: (groupId: string) => void;
    onNavigate: (url: string) => void;
}

function buildGroupActions({
    group,
    onUnconsolidate,
    onNavigate,
}: BuildGroupActionsParams): CommandAction[] {
    return [
        {
            key: "view-reporting",
            label: "View Reporting",
            onClick: () =>
                onNavigate(buildReportingConsolidationRoute(group.id)),
        },
        {
            key: "unconsolidate",
            label: "Unconsolidate",
            onClick: () => onUnconsolidate(group.id),
        },
    ];
}

interface BuildRelationshipActionsParams {
    relationship: ConsolidationRelationshipResponse;
    onEditRelationship: (
        relationship: ConsolidationRelationshipResponse,
    ) => void;
    onDeleteRelationship: (relationshipId: string) => void;
}

function buildRelationshipActions({
    relationship,
    onEditRelationship,
    onDeleteRelationship,
}: BuildRelationshipActionsParams): CommandAction[] {
    return [
        {
            key: "edit",
            label: "Edit",
            onClick: () => onEditRelationship(relationship),
        },
        {
            key: "delete",
            label: "Delete",
            onClick: () => onDeleteRelationship(relationship.id),
        },
    ];
}
